import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { Tab, Tabs, Tooltip, OverlayTrigger } from 'react-bootstrap';
import Fa from 'react-fontawesome';
import moment from 'moment';
import { filter as lodashFilter, find, first, reduce, map, intersection, indexOf } from 'lodash';

import requiredSetupMappings from '../../../../components/required-setup-mappings';
import ProgressBar from '../../../../components/progress-bar';
import DistributionGraph from '../../components/distribution-graph';
import SalesRepStats from '../../components/stats';
import requiredSync from '../../../../components/required-sync';
import orgAwearness from '../../../../components/organization-awearness';
import Quadrant from '../../components/quadrant/Quadrant';
import OpportunityTable from '../../components/opportunity-table';
import OpportunityModal from '../../components/opportunity-modal';
import NumberDisplay from '../../../../components/number-display';
import SearchForm from '../../../../components/search-form';
import { getForecastCategoryFilter } from '../../../../components/search-form/search-form-reducer';
import SalesTenureGraph from '../../components/sales-tenure-graph';
import ForecastAccuracyField from '../../components/forecast-accuracy-field';

// Import Actions
import { fetchOpportunities, reset, fetchForecast } from '../../PerformanceActions';
import { fetchForcastPeriods } from '../../../Forecast/ForecastActions';
// Import Selectors
import { getOutcome, getLoading, getLoaded, getErrors, getHasErrors } from '../../PerformanceReducer';

import styles from './styles.css';

const USER_PREFERENCE_PERIOD = 'performance-forecast-period';
const USER_PREFERENCE_CATEGORY = 'performance-forecast-category';

class PerformancePage extends Component {
  constructor() {
    super();
    this.state = {
      criteria: null,
      forecastClosedMonths: 0,
      minForecastDate: null,
      maxForecastDate: null,
      selectedOwners: [],
      loadingForecast: false,
      selectedForecastPeriod: null,
      selectedForecastCategories: [],
      loadingPeriod: true,
      selectedTab: 'quadrant',
    };
    this.handleFormChange = this.handleFormChange.bind(this);
    this.hanldeReset = this.hanldeReset.bind(this);
    this.handleChartSegmentSelect = this.handleChartSegmentSelect.bind(this);
    this.handleForecastParamChange = this.handleForecastParamChange.bind(this);
    this.onTabSelect = this.onTabSelect.bind(this);
    this.hanldeSearchContextChange = this.hanldeSearchContextChange.bind(this);
  }

  componentWillMount() {
    const { getUserPreference } = this.context;
    const selectedForecastPeriod = getUserPreference(USER_PREFERENCE_PERIOD, 'amount', true);
    const selectedForecastCategories = getUserPreference(USER_PREFERENCE_CATEGORY, [], true, true);
    this.setState({
      selectedForecastPeriod, selectedForecastCategories,
    });
  }

  componentDidMount() {
    this.props.dispatch(reset()).then(() => {
      this.mounted = true;
    });
    this.fetchPeriods();
    this.checkProps(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this.checkProps(nextProps);
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  onTabSelect(selectedTab) {
    this.setState({ selectedTab });
  }

  getValidForcastMappingIds() {
    const { selectedForecastCategories } = this.state;
    const { forecastCategoryFilter } = this.props;
    if (forecastCategoryFilter) {
      const { mappings } = forecastCategoryFilter;
      const availableMappingIds = map(mappings, '_id');
      const requestedMappingIds = intersection(selectedForecastCategories, availableMappingIds);
      return requestedMappingIds;
    }
    return selectedForecastCategories;
  }

  getForecastData() {
    const { forecastCategoryFilter } = this.props;
    const {
      selectedForecastPeriod,
      minForecastDate, maxForecastDate, criteria,
    } = this.state;
    this.context.updateUserPreference(USER_PREFERENCE_PERIOD, selectedForecastPeriod, true);
    if (forecastCategoryFilter) {
      const requestedMappingIds = this.getValidForcastMappingIds();
      this.context.updateUserPreference(USER_PREFERENCE_CATEGORY, requestedMappingIds, true, true);
      const params = {
        ...criteria,
        forecast: true,
        period: [minForecastDate, maxForecastDate],
      };

      const forecastCategoryParams = {
        _id: forecastCategoryFilter._id,
        type: 'include',
        values: [],
        mappings: requestedMappingIds,
      };
      this.setState({ loadingForecast: true });
      this.props.dispatch(fetchForecast(params, forecastCategoryParams))
        .then(
          () => {
            this.setState({ loadingForecast: false });
          },
          () => {
            this.setState({ loadingForecast: false });
          },
        );
    }
  }

  checkProps(props) {
    const { selectedForecastCategories } = this.state;
    if (selectedForecastCategories.length === 0) {
      const { forecastCategoryFilter } = props;
      const options = reduce(forecastCategoryFilter ? forecastCategoryFilter.mappings : [], (items, mapping) => {
        items.push({
          ...mapping,
        });
        return items;
      }, []);
      const commitOption = find(options, { label: 'Commit' }) || first(options);
      if (commitOption) {
        const categories = [commitOption._id];
        this.setState({ selectedForecastCategories: categories });
      }
    }
  }

  fetchPeriods() {
    this.setState({ loadingPeriod: true });
    this.props.dispatch(fetchForcastPeriods({ all: true }))
      .then(({ periods, min, max }) => {
        let diff = 0;
        if (periods && periods.length) {
          const minDate = moment(periods[0], 'YYYY-MM-DD');
          let now = moment();
          const endOfMonth = moment().endOf('month');
          if (!now.isSame(endOfMonth, 'day')) {
            now = moment().subtract(1, 'months').endOf('month');
          }
          diff = now.diff(minDate, 'months');
        }
        this.setState({
          forecastClosedMonths: diff, minForecastDate: min, maxForecastDate: max, loadingPeriod: false,
        });
      });
  }

  hanldeReset() {
    this.props.dispatch(reset());
  }

  hanldeSearchContextChange(params) {
    const state = {};
    if (params.selectedForecastPeriod) {
      state.selectedForecastPeriod = params.selectedForecastPeriod;
    }
    if (params.selectedForecastCategories) {
      state.selectedForecastCategories = params.selectedForecastCategories ? params.selectedForecastCategories.split(',') : [];
    }
    this.setState(state);
  }

  handleForecastParamChange(selectedForecastPeriod, selectedForecastCategories) {
    this.setState({ selectedForecastPeriod, selectedForecastCategories });
  }

  handleFormChange(criteria) {
    if (this.mounted) {
      this.props.dispatch(fetchOpportunities(criteria)).then((results) => {
        this.setState({ criteria });
        const { opportunities, total } = results;
        const returnResults = opportunities.length;
        if (total && returnResults < total) {
          const el = (
            <div>
              Your search return more than <NumberDisplay value={total} precision={0} /> results.<br />
              For optimal performance, only the latest <NumberDisplay value={returnResults} precision={0} /> will be displayed.
            </div>
          );
          this.context.addNotification(el, { type: 'warning', autoClose: 30 * 1000 });
        }
        this.getForecastData();
      });
    }
  }

  handleChartSegmentSelect(selectedOwnerIds) {
    const { stats } = this.props;
    const selectedOwners = lodashFilter(stats, s => indexOf(selectedOwnerIds, s.owner_id) >= 0);
    this.setState({ selectedOwners });
  }

  renderError() {
    const { errors, hasErrors } = this.props;
    if (!hasErrors) {
      return null;
    }
    let message = errors.messge;
    if (errors.data) {
      message = errors.data.message || errors.data;
    }
    return (
      <div className="alert alert-danger">
        <Fa name="exclamation-triangle" /> {message}
      </div>
    );
  }


  render() {
    const {
      loading, loaded, stats, hasErrors, forecastCategoryFilter,
    } = this.props;
    const { loadingPeriod, selectedOwners } = this.state;
    const quadrantPopovers = (
      <Tooltip id="quadrant-perf-tooltip">
        The performance quadrant groups active sales representatives into four quadrants
        based on their individual performance compared with the average for the sales team.
        It is important to note that only active sales representatives with at least three
        closed opportunities are included in the chart.
      </Tooltip>
    );

    const distPopovers = (
      <Tooltip id="dist-tooltip">
        Distribution of outcome rates and forecast accuracy
      </Tooltip>
    );

    const tenurePopovers = (
      <Tooltip id="dist-tooltip">
        Sales rep tenure distribution
      </Tooltip>
    );

    const {
      criteria, minForecastDate, maxForecastDate,
      forecastClosedMonths, loadingForecast,
      selectedForecastPeriod,
    } = this.state;

    const forecastMappingIds = this.getValidForcastMappingIds();

    const displayForecastData = forecastClosedMonths > 0 && forecastCategoryFilter && forecastCategoryFilter.mappings.length > 0;
    return (
      <div>
        <Helmet title="Sales Performance" />
        <div className="container">
          <div className="clearfix">
            <div className="pull-right page-header-control">
              {!loadingPeriod &&
                <SearchForm
                  onSearchChange={this.handleFormChange}
                  onReset={this.hanldeReset}
                  disabled={loading}
                  pullRight
                  loadRecommendedCriteria
                  type="performance"
                  disabledRootLogic={displayForecastData}
                  searchContext={{
                    selectedForecastPeriod,
                    selectedForecastCategories: forecastMappingIds ? forecastMappingIds.join(',') : forecastMappingIds,
                  }}
                  onSearchContextChange={this.hanldeSearchContextChange}
                >
                  {displayForecastData &&
                    <ForecastAccuracyField
                      forecastPeriod={this.state.selectedForecastPeriod}
                      forecastCategories={this.state.selectedForecastCategories}
                      onChange={this.handleForecastParamChange}
                      forecastClosedMonths={forecastClosedMonths}
                      forecastCategoryFilter={forecastCategoryFilter}
                      disabled={loadingForecast}
                    />
                  }
                </SearchForm>
              }
              {loadingPeriod && <Fa name="spinner" className="text-muted" spin />}
            </div>
            <h1 className="page-header">
              Sales Performance
            </h1>
          </div>
          {loading && <ProgressBar />}
          {!loading && !loaded && hasErrors && this.renderError()}
          {!loading && loaded && stats.length > 0 && <SalesRepStats />}
        </div>

        {!loading && loaded && stats.length > 0 &&
          <div className={styles.graphWrapper}>
            <div className="container">
              <Tabs
                defaultActiveKey={this.state.selectedTab}
                id="performance-tabs"
                animation
                mountOnEnter
                unmountOnExit
                onSelect={this.onTabSelect}
              >
                <Tab
                  eventKey="quadrant"
                  title={
                    <span>
                      <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={quadrantPopovers}>
                        <span>Quadrant</span>
                      </OverlayTrigger>
                    </span>
                  }
                >
                  <div>
                    {loadingForecast && <div><p /><ProgressBar /></div>}
                    {!loadingForecast &&
                      <Quadrant
                        criteria={criteria}
                        minForecastDate={minForecastDate}
                        maxForecastDate={maxForecastDate}
                        forecastClosedMonths={forecastClosedMonths}
                        onChartSegmentSelect={this.handleChartSegmentSelect}
                        displayForecastData={displayForecastData}
                        forecastKey="forecast"
                      />
                    }
                  </div>
                </Tab>
                <Tab
                  eventKey="distribution"
                  title={
                    <span>
                      <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={distPopovers}>
                        <span>Distribution</span>
                      </OverlayTrigger>
                    </span>
                  }
                >
                  <div>
                    {loadingForecast && <div><p /><ProgressBar /></div>}
                    {!loadingForecast &&
                      <DistributionGraph
                        criteria={criteria}
                        minForecastDate={minForecastDate}
                        maxForecastDate={maxForecastDate}
                        forecastClosedMonths={forecastClosedMonths}
                        onChartSegmentSelect={this.handleChartSegmentSelect}
                        displayForecastData={displayForecastData}
                        forecastKey="forecast"
                      />
                    }
                  </div>
                </Tab>
                <Tab
                  eventKey="tenure"
                  title={
                    <span>
                      <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={tenurePopovers}>
                        <span>Sales Tenure</span>
                      </OverlayTrigger>
                    </span>
                  }
                >
                  <div>
                    {loadingForecast && <div><p /><ProgressBar /></div>}
                    {!loadingForecast &&
                      <SalesTenureGraph
                        criteria={criteria}
                        minForecastDate={minForecastDate}
                        maxForecastDate={maxForecastDate}
                        forecastClosedMonths={forecastClosedMonths}
                        onChartSegmentSelect={this.handleChartSegmentSelect}
                        displayForecastData={displayForecastData}
                        forecastKey="forecast"
                      />
                    }
                  </div>
                </Tab>
              </Tabs>
            </div>
          </div>
        }

        {!loading && loaded &&
          <div className="container">
            <OpportunityTable
              stats={stats}
              forecastClosedMonths={forecastClosedMonths}
              forecastPeriod={this.state.selectedForecastPeriod}
              forecastKey="forecast"
            />
            <OpportunityModal
              stats={selectedOwners}
              onClose={() => {
                this.setState({
                  selectedOwners: [],
                });
              }}
              tableConfigs={{
                forecastClosedMonths,
                forecastPeriod: this.state.selectedForecastPeriod,
                forecastKey: 'forecast',
              }}
            />
          </div>
        }
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  const forecastCategoryFilter = getForecastCategoryFilter(state);
  return {
    stats: getOutcome(state),
    loading: getLoading(state),
    loaded: getLoaded(state),
    errors: getErrors(state),
    hasErrors: getHasErrors(state),
    forecastCategoryFilter: {
      ...forecastCategoryFilter,
      mappings: lodashFilter(forecastCategoryFilter ? forecastCategoryFilter.mappings : [], mapping => (mapping.label !== 'Closed' &&
        mapping.label !== 'Omitted' &&
        mapping.values &&
        mapping.values.length > 0)),
    },
  };
}

PerformancePage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  stats: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loading: PropTypes.bool.isRequired,
  loaded: PropTypes.bool.isRequired,
  errors: PropTypes.shape({}),
  hasErrors: PropTypes.bool.isRequired,
  forecastCategoryFilter: PropTypes.shape({
    mappings: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string.isRequired,
    })),
  }),
};

PerformancePage.defaultProps = {
  errors: null,
  forecastCategoryFilter: null,
};

PerformancePage.contextTypes = {
  router: PropTypes.object,
  addNotification: PropTypes.func.isRequired,
  updateUserPreference: PropTypes.func.isRequired,
  getUserPreference: PropTypes.func.isRequired,
};
export default connect(mapStateToProps)(orgAwearness(requiredSync(requiredSetupMappings(PerformancePage, ['stage']))));
